import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { uniTestkitFactoryCreator } from '@wix/wix-ui-test-utils/vanilla';
import { DatePickerInput, DatePickerInputProps } from '..';
import { datePickerInputDriverFactory } from './DatePickerInput.driver';
import { classes } from './DatePickerInput.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper/types';
import { EditorShell } from '../../test/visual/VisualAsync';

const createDriver = uniTestkitFactoryCreator(datePickerInputDriverFactory);
const dataHook = 'storybook-datePickerInput';

interface DatePickerInputVisualProps {
  datePickerInputProps: DatePickerInputProps;
  style?: Record<string, string>;
  onDone(): void;
}

class DatePickerInputVisualComponent extends React.Component<DatePickerInputVisualProps> {
  static defaultProps = {
    datePickerInputProps: {},
    onDone: () => {},
  };

  async componentDidMount() {
    const { onDone } = this.props;
    const driver = createDriver({ wrapper: document.body, dataHook });

    await driver.openCalendarWindow();

    onDone();
  }
  render() {
    const { datePickerInputProps, style } = this.props;

    return (
      <EditorShell>
        <div style={style}>
          <DatePickerInput data-hook={dataHook} {...datePickerInputProps} />
        </div>
      </EditorShell>
    );
  }
}

visualize('DatePickerInput', () => {
  story('simple example', () => {
    snap('default props', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap(
      'Without a selected date',
      <DatePickerInput
        value={null}
        onChange={() => {}}
        placeholderText="Select A Date"
      />,
    );
    snap('FilterDate - Prior for today dates (includes today)', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          filterDate: (date) => date <= new Date('2020/10/10'),
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('FilterDate - feature dates only (includes today)', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          filterDate: (date) => date >= new Date('2020/10/10'),
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('Months dropdown', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          showMonthDropdown: true,
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('Years dropdown', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          showYearDropdown: true,
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('Months and years dropdown', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          showMonthDropdown: true,
          showYearDropdown: true,
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap(
      'Disabled Mode',
      <DatePickerInput
        value={new Date('2020/09/20')}
        onChange={() => {}}
        placeholderText="Select A Date"
        disabled
      />,
    );
    snap(
      'Error mode',
      <DatePickerInput
        value={new Date('2020/09/20')}
        onChange={() => {}}
        placeholderText="Select A Date"
        hasError
        errorMessage="Error Message"
      />,
    );
    snap(
      'New error mode',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with message',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with message / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles and message',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles and message / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap('Open with new error message', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          onChange: () => {},
          hasError: true,
          errorMessage: 'Error Message',
          newErrorMessage: true,
        }}
        onDone={done}
      />
    ));
    snap(
      'Open with new error message / ErrorAppearance - BackgroundAndBorder',
      (done) => (
        <DatePickerInputVisualComponent
          datePickerInputProps={{
            value: new Date('2020/10/10'),
            placeholderText: 'Select A Date',
            onChange: () => {},
            hasError: true,
            errorMessage: 'Error Message',
            errorAppearance: ErrorAppearance.BackgroundAndBorder,
            newErrorMessage: true,
          }}
          onDone={done}
        />
      ),
    );
    snap('With label', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          onChange: () => {},
          label: 'Label',
        }}
        onDone={done}
      />
    ));
    snap('With custom border', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/09/20'),
          onChange: () => {},
          className: classes.rootWithCustomBorder,
        }}
        onDone={done}
      />
    ));
    snap('With design overrides', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/09/20'),
          onChange: () => {},
          className: classes.rootWithDesignOverrides,
        }}
        onDone={done}
      />
    ));
  });

  story('wst variables', () => {
    snap(
      'primary background',
      <div
        style={
          {
            '--wst-primary-background-color-rgb': '0,255,0',
          } as any
        }
      >
        <DatePickerInput
          value={new Date('2020/10/10')}
          onChange={() => {}}
          label="I'm a label for this input"
        />
      </div>,
    );

    snap(
      'shade 3',
      <div
        style={
          {
            '--wst-shade-3-color-rgb': '0,255,0',
          } as any
        }
      >
        <DatePickerInput
          onChange={() => {}}
          label="I'm a label for this input"
        />
      </div>,
    );

    snap(
      'paragraph 2 color',
      <div
        style={
          {
            '--wst-paragraph-2-color-rgb': '0,255,0',
          } as any
        }
      >
        <DatePickerInput
          value={new Date('2020/10/10')}
          onChange={() => {}}
          label="I'm a label for this input"
        />
      </div>,
    );

    snap(
      'paragraph 2 font',
      <div
        style={
          {
            '--wst-paragraph-2-font-family': 'Times New Roman',
            '--wst-paragraph-2-font-size': '100px',
          } as any
        }
      >
        <DatePickerInput
          value={new Date('2020/10/10')}
          onChange={() => {}}
          label="I'm a label for this input"
        />
      </div>,
    );

    snap(
      'system disabled color',
      <div
        style={
          {
            '--wst-system-disabled-color-rgb': '0,255,0',
          } as any
        }
      >
        <DatePickerInput
          value={new Date('2020/10/10')}
          onChange={() => {}}
          disabled
          label="I'm a label for this input"
        />
      </div>,
    );

    snap(
      'system error color',
      <div
        style={
          {
            '--wst-system-error-color-rgb:': '0,255,0',
          } as any
        }
      >
        <DatePickerInput
          value={new Date('2020/10/10')}
          onChange={() => {}}
          disabled
          hasError
          errorMessage="I'm an error message"
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          label="I'm a label for this input"
        />
      </div>,
    );
  });
});
